import { predicate } from '@prismicio/client'
import type { BuildQueryURLArgs } from '@prismicio/client'

import { lang } from '@lib/constants'
import { createClient } from '../../../prismicio'
import type { AllDocumentTypes } from '../../../../prismicio-types'

// List all Prismic custom type API IDs used in your project
export type PrismicCustomType =
	| 'home_rebrand'
	| 'page_rebrand'
	| 'article'
	| 'magazine'
	| 'members_market'
	| 'subscription_listing'
	| 'subscription_detail'
	| 'survey_questions'
	| 'sms_landing_page'
	| 'box_configuration'
	| 'customization_configuration'
	| 'product_group'
	| 'promo_bar'
	| 'topic'
	| 'header_contents'
	| 'get_started_offer'

export async function getDocumentByUID(
	docType: AllDocumentTypes['type'],
	uid: string,
	refOption?: Partial<BuildQueryURLArgs> | undefined,
	previewData?: any
) {
	const client = createClient({ previewData })
	const document = await client
		.getByUID(docType, uid, { ...refOption, lang })
		.catch((err) => console.log(err))

	return document || null
}

export async function getAllPathsByType(docType: AllDocumentTypes['type'], previewData?: any) {
	const client = createClient({ previewData })
	const documents = await client.getAllByType(docType, { lang })

	const paths = documents.map((doc) => {
		const { uid } = doc
		return { params: { uid } }
	})
	return paths
}

// Nested slice data
export const sliceFetchLinks = ['product_group.title', 'product_group.products']

// Homepage
export default function getHomeContentByUID(type: AllDocumentTypes['type'], previewData: any) {
	const client = createClient({ previewData })
	return client
		.getSingle(type, {
			fetchLinks: sliceFetchLinks,
			lang
		})
		.catch((err) => console.log(err))
}

// Header Contents (singleton)
export async function getHeaderContents(previewData?: any) {
	const client = createClient({ previewData })
	return client
		.getSingle('header_contents' as any, {
			lang
		})
		.catch((err) => {
			console.log('Error fetching header_contents:', err)
			return null
		})
}

// Magazine/Articles
export function getMagContent({
	topicID,
	after,
	withBodyContent = true,
	previewData
}: {
	topicID?: string
	after?: string
	withBodyContent?: boolean
	previewData?: any
}) {
	const client = createClient({ previewData })

	return client.getByType('article', {
		graphQuery: `{
				article {
					hero_image
					og_image
					description
					intro
					title

					author {
						...on person {
							name
							photo
						}
					}

					topics {
						topic {
							...on topic {
								title
							}
						}
					}

					${
						withBodyContent
							? `body {
						...on full_width_text {
							non-repeat {
								...non-repeatFields
							}
							repeat {
								...repeatFields
							}
						}
					}`
							: ''
					}
				}
			}`,
		pageSize: 50,
		predicates: topicID && [predicate.at('my.article.topics.topic', topicID)],
		orderings: ['my.article.date desc'],
		after,
		lang
	})
}
export function getMagDoc(previewData?: any) {
	const client = createClient({ previewData })
	return client
		.getSingle('magazine', {
			graphQuery: `{
			magazine {
				...magazineFields
				
				featured_articles {
					article {
						...articleFields

						topics {
							topic {
								...on topic {
									...topicFields
								}
							}
						}
					}
				}
				featured_topics {
					topic {
						...on topic {
							...topicFields
						}
					}
				}
			}
		}`,
			lang
		})
		.catch((err) => console.log(err))
}
export function getMagTopics(previewData?: any) {
	const client = createClient({ previewData })
	return client.getAllByType('topic', { lang }).then((topics) =>
		topics.reduce(
			(arr, topic) => {
				const { uid, id, data } = topic || {}
				const { title } = data || {}

				// Extract plain text from the first RichText node if available
				let firstText = ''
				if (Array.isArray(title) && title.length > 0 && title[0] && 'text' in title[0]) {
					firstText = (title[0] as any).text
				}

				if (uid && id && firstText) {
					arr.push({ uid, id, data: { title: [{ text: firstText }] } })
				}

				return arr
			},
			[] as { uid: string; id: string; data: { title: { text: string }[] } }[]
		)
	)
}
